import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { PageControl } from "components/shared/PageControl";
import { defaultPageSize } from "helpers";
import React from "react";
import { BASE_API_URL } from "store/Me";

export default function LockEvents({ lockId }: { lockId: string }) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const { data, isError } = useGetLockEvents(lockId, page, pageSize);

  if (isError) {
    return <div>Error loading events</div>;
  }

  if (data) {
    if (data.length === 0) {
      return <div>No events found</div>;
    }

    return (
      <div className="pb-5 space-y-3">
        <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col">Event</th>
                  <th scope="col">Time</th>
                  <th scope="col">Description</th>
                  <th scope="col">Data</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                {data?.map((event, index) => (
                  <tr key={index}>
                    <td>{event.event}</td>
                    <td>{new Date(event.time).toLocaleString()}</td>
                    <td>{event.description}</td>
                    <td>{event.data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <PageControl
          onNext={() => setPage(page + 1)}
          onPrev={() => {
            if (page > 0) {
              setPage(page - 1);
            }
          }}
          setPageSize={(pageSize) => {
            setPageSize(pageSize);
            setPage(0);
          }}
          pageSize={pageSize || defaultPageSize}
          currentPage={page}
          disablePrev={page === 0}
          disableNext={data.length < pageSize}
        />
      </div>
    );
  }

  return <div>loading...</div>;
}

interface LockEventResponse {
  event: string;
  time: string;
  description: string;
  data: string;
}

async function getLockEvents(lockId: string, page: number, pageSize: number) {
  const params = new URLSearchParams();
  const skip = page * pageSize;
  params.append("skip", skip.toString());
  params.append("take", pageSize.toString());

  const response = await axios.get<LockEventResponse[]>(
    `${BASE_API_URL}/api/Locks/${lockId}/events?${params}`
  );
  return response.data;
}

function useGetLockEvents(lockId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: ["locks", "details", "events", lockId, page, pageSize],
    queryFn: () => getLockEvents(lockId, page, pageSize),
  });
}
